import { store, persistor } from "@/redux/store";
import { PersistGate } from "redux-persist/integration/react";
import "bootstrap/dist/css/bootstrap.css";

import "@/assets/styles/index.scss";
import "@/assets/styles/index.global.scss";

import { ANALYTICS, VWO } from "@/config/environment";
import {
  VWO_HIDE_STYLE_ID,
  VWO_PRECONNECT_ID,
  VWO_SCRIPT_ID,
  getVwoScript,
} from "@/config/vwoScript";
import Head from "next/head";
import PropTypes from "prop-types";
import { StrictMode, useEffect } from "react";
import { Provider } from "react-redux";
import { useSelector } from "react-redux";

import Loader from "@/components/core/Loader";
import Seo from "@/components/Seo";
import ErrorBoundary from "@/components/core/error-boundary";
import ErrorModal from "@/components/core/ErrorModal";
import Authorize from "@/components/core/authorize";
import Layout from "@/components/core/layout";
import { USER_TYPES } from "@/config/constants";
import { getCustomerType, getMetadata } from "@/features/metadata/metadataSlice";
import { initializeGA, pageView } from "@/helpers/analytics";
import "../i18n";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function VwoManager() {
  const metadata = useSelector(getMetadata);
  const customerType = useSelector(getCustomerType);
  const isVwoEnabled = ANALYTICS.IS__VWO_ENABLED;
  const isCustomerFlow =
    customerType?.toLowerCase() === USER_TYPES.CUSTOMER ||
    metadata?.userType?.toLowerCase() === USER_TYPES.CUSTOMER;

  useEffect(() => {
    const removeVwo = () => {
      document.getElementById(VWO_SCRIPT_ID)?.remove();
      document.getElementById(VWO_PRECONNECT_ID)?.remove();
      document.getElementById(VWO_HIDE_STYLE_ID)?.remove();
      delete window._vwo_code;
      delete window._vwo_settings_timer;
      delete window.VWO;
    };

    if (!isVwoEnabled || !isCustomerFlow) {
      removeVwo();
      return undefined;
    }

    if (!document.getElementById(VWO_PRECONNECT_ID)) {
      const preconnect = document.createElement("link");
      preconnect.id = VWO_PRECONNECT_ID;
      preconnect.rel = "preconnect";
      preconnect.href = VWO.BASE_URL;
      document.head.appendChild(preconnect);
    }

    if (!document.getElementById(VWO_SCRIPT_ID)) {
      const script = document.createElement("script");
      script.id = VWO_SCRIPT_ID;
      script.type = "text/javascript";
      script.text = getVwoScript({
        accountId: VWO.ACCOUNT_ID,
        baseUrl: VWO.BASE_URL,
      });
      document.head.appendChild(script);
    }

    return undefined;
  }, [isCustomerFlow, isVwoEnabled]);

  return null;
}

export default function App({ Component, pageProps }) {
  useEffect(() => {
    initializeGA();
    pageView("my-renewal");
  }, []);

  return (
    <StrictMode>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <VwoManager />
          <Head>
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1"
            />
          </Head>
          <Seo
            desc="Login to Tata AIG Renewal to renew your Car, Bike, Health &amp; Travel policy online instantly. Download duplicate policy, intimate &amp; track claim."
            keywords="renew edit policy details"
            title="Tata AIG Renew Policy | Tata AIG Login"
          />
          <ErrorBoundary>
            <Authorize>
              <Layout>
                <Loader />
                <ErrorModal />
                <Component {...pageProps} />
                <ToastContainer
                  position="top-right"
                  autoClose={5000}
                  hideProgressBar={false}
                  newestOnTop={false}
                  closeOnClick
                  rtl={false}
                  pauseOnFocusLoss
                  draggable
                  pauseOnHover
                />
              </Layout>
            </Authorize>
          </ErrorBoundary>
        </PersistGate>
      </Provider>
    </StrictMode>
  );
}

App.propTypes = {
  Component: PropTypes.func,
  pageProps: PropTypes.object,
};
